import React from "react";
import { api, photoUrl, videoUrl, videoPosterUrl } from "../lib/api";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";

const FALLBACK_PHOTOS = [
  "https://images.pexels.com/photos/19075389/pexels-photo-19075389.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  "https://images.pexels.com/photos/2506990/pexels-photo-2506990.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  "https://images.pexels.com/photos/21856213/pexels-photo-21856213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  "https://images.unsplash.com/photo-1750271329214-a7dbce880e85?crop=entropy&cs=srgb&fm=jpg&q=85",
];

function PhotoCarousel({ items }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: items.length > 1, align: "start" });
  const [lightbox, setLightbox] = React.useState(null);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  const basis =
    items.length === 1
      ? "flex-[0_0_100%]"
      : items.length === 2
      ? "flex-[0_0_100%] sm:flex-[0_0_50%]"
      : "flex-[0_0_100%] sm:flex-[0_0_60%] lg:flex-[0_0_45%]";

  return (
    <div className="relative">
      <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="flex">
          {items.map((it, i) => (
            <div
              key={i}
              className={`relative ${basis} pr-3 last:pr-0`}
            >
              <button
                onClick={() => setLightbox(it)}
                className="group block w-full overflow-hidden rounded-2xl border border-[var(--border)] bg-[#13151D]"
                data-testid={`gallery-photo-${i}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={it.src}
                    alt={it.caption || `Photo ${i + 1}`}
                    loading={i < 3 ? "eager" : "lazy"}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  {it.caption && (
                    <div className="absolute bottom-3 left-4 right-4 text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      {it.caption}
                    </div>
                  )}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {items.length > 1 && (
        <>
          <button
            onClick={scrollPrev}
            aria-label="Previous"
            data-testid="photo-carousel-prev"
            className="hidden md:grid place-items-center w-11 h-11 rounded-full glass border border-white/10 hover:border-[#C5A059] hover:text-[#C5A059] absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next"
            data-testid="photo-carousel-next"
            className="hidden md:grid place-items-center w-11 h-11 rounded-full glass border border-white/10 hover:border-[#C5A059] hover:text-[#C5A059] absolute right-3 top-1/2 -translate-y-1/2 z-10 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-black/90 grid place-items-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="photo-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-5 right-5 text-white/70 hover:text-white"
              aria-label="Close"
            >
              <X size={24} />
            </button>
            <motion.img
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              src={lightbox.src}
              alt={lightbox.caption || ""}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[92vw] max-h-[88vh] object-contain rounded-xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function VideoCarousel({ items }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: items.length > 1, align: "start" });
  const [active, setActive] = React.useState(null);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  const basis =
    items.length === 1
      ? "flex-[0_0_100%]"
      : items.length === 2
      ? "flex-[0_0_100%] sm:flex-[0_0_50%]"
      : "flex-[0_0_100%] sm:flex-[0_0_60%] lg:flex-[0_0_45%]";

  return (
    <div className="relative">
      <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="flex">
          {items.map((it, i) => (
            <div
              key={it.id}
              className={`relative ${basis} pr-3 last:pr-0`}
            >
              <button
                onClick={() => setActive(it)}
                className="group block w-full overflow-hidden rounded-2xl border border-[var(--border)] bg-[#13151D]"
                data-testid={`gallery-video-${i}`}
              >
                <div className="relative aspect-video overflow-hidden">
                  {it.poster ? (
                    <img
                      src={it.poster}
                      alt={it.caption || `Video ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <video
                      src={it.src}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="w-16 h-16 rounded-full bg-[#C5A059]/90 backdrop-blur-sm grid place-items-center group-hover:scale-110 group-hover:bg-[#C5A059] transition-all shadow-2xl">
                      <Play size={24} fill="#0A0B10" stroke="#0A0B10" />
                    </div>
                  </div>
                  {it.caption && (
                    <div className="absolute bottom-3 left-4 right-4 text-white text-sm font-medium">
                      {it.caption}
                    </div>
                  )}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {items.length > 1 && (
        <>
          <button
            onClick={scrollPrev}
            aria-label="Previous"
            data-testid="video-carousel-prev"
            className="hidden md:grid place-items-center w-11 h-11 rounded-full glass border border-white/10 hover:border-[#C5A059] hover:text-[#C5A059] absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next"
            data-testid="video-carousel-next"
            className="hidden md:grid place-items-center w-11 h-11 rounded-full glass border border-white/10 hover:border-[#C5A059] hover:text-[#C5A059] absolute right-3 top-1/2 -translate-y-1/2 z-10 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-black/95 grid place-items-center p-4"
            onClick={() => setActive(null)}
            data-testid="video-lightbox"
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 text-white/70 hover:text-white z-10"
              aria-label="Close"
            >
              <X size={24} />
            </button>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl">
              <video
                src={active.src}
                poster={active.poster || undefined}
                controls
                autoPlay
                playsInline
                className="w-full max-h-[85vh] rounded-xl bg-black"
              />
              {active.caption && (
                <p className="text-center text-[#A1A4B5] text-sm mt-3">{active.caption}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Gallery() {
  const [tab, setTab] = React.useState("photos");
  const [photos, setPhotos] = React.useState([]);
  const [videos, setVideos] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    Promise.all([
      api.get("/photos").then((r) => setPhotos(r.data || [])).catch(() => setPhotos([])),
      api.get("/videos").then((r) => setVideos(r.data || [])).catch(() => setVideos([])),
    ]).finally(() => setLoaded(true));
  }, []);

  const photoItems = photos.length
    ? photos.map((p) => ({ src: photoUrl(p.id), caption: p.caption || "" }))
    : FALLBACK_PHOTOS.map((src) => ({ src, caption: "" }));

  const videoItems = videos.map((v) => ({
    id: v.id,
    src: videoUrl(v.id),
    poster: v.poster_path ? videoPosterUrl(v.id) : "",
    caption: v.caption || "",
  }));

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-16 md:py-32 px-5 sm:px-6 md:px-12 lg:px-24 max-w-7xl mx-auto"
    >
      <div className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-4 md:gap-6">
        <div>
          <p className="overline mb-3 md:mb-4">Visual Tour</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            The Villa <span className="italic text-[#C5A059]">in Frames</span>
          </h2>
        </div>

        <div
          className="inline-flex items-center p-1 rounded-full border border-[var(--border)] bg-[#13151D]"
          role="tablist"
          data-testid="gallery-tabs"
        >
          <button
            onClick={() => setTab("photos")}
            data-testid="gallery-tab-photos"
            className={`px-4 sm:px-5 py-1.5 sm:py-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] rounded-full transition-all ${
              tab === "photos"
                ? "bg-[#C5A059] text-black font-semibold"
                : "text-[#A1A4B5] hover:text-white"
            }`}
          >
            Photos {photos.length > 0 && <span className="opacity-70">· {photos.length}</span>}
          </button>
          <button
            onClick={() => setTab("videos")}
            data-testid="gallery-tab-videos"
            className={`px-4 sm:px-5 py-1.5 sm:py-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] rounded-full transition-all ${
              tab === "videos"
                ? "bg-[#C5A059] text-black font-semibold"
                : "text-[#A1A4B5] hover:text-white"
            }`}
          >
            Videos {videos.length > 0 && <span className="opacity-70">· {videos.length}</span>}
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
        >
          {tab === "photos" ? (
            <PhotoCarousel items={photoItems} />
          ) : videoItems.length > 0 ? (
            <VideoCarousel items={videoItems} />
          ) : (
            <div className="rounded-2xl border border-dashed border-[var(--border)] py-20 text-center text-[#A1A4B5]">
              <p className="font-serif-display text-2xl text-white">No videos yet</p>
              <p className="text-sm mt-2">Videos will appear here once uploaded from the admin panel.</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {!loaded && (
        <p className="text-center text-[#A1A4B5] mt-6 text-sm">Loading gallery…</p>
      )}
    </section>
  );
}
